import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';

interface SearchableSelectProps {
  options: string[];
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  emptyLabel?: string;
  className?: string;
  disabled?: boolean;
}

// The option panel is portaled to <body> and positioned in viewport
// coordinates, so an `overflow: hidden` ancestor (e.g. the Comparison page's
// Advanced Filters drawer) can never clip it.
const MENU_MAX_HEIGHT = 288; // Tailwind max-h-72
const MENU_GAP = 4;          // Tailwind mt-1
const VIEWPORT_MARGIN = 8;
const MIN_MENU_HEIGHT = 96;

interface MenuPosition {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  emptyLabel,
  className = '',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pos, setPos] = useState<MenuPosition | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const listboxId = useId();

  const filtered = search
    ? options.filter((o) => o.toLowerCase().includes(search.toLowerCase()))
    : options;

  // Measure the trigger and derive the panel geometry. Flips above the
  // trigger when the viewport has less room below than above.
  const updatePosition = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return; // not measurable
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const spaceBelow = vh - rect.bottom - MENU_GAP - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - MENU_GAP - VIEWPORT_MARGIN;
    const flip = spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow;
    const maxHeight = Math.max(
      MIN_MENU_HEIGHT,
      Math.min(MENU_MAX_HEIGHT, flip ? spaceAbove : spaceBelow),
    );
    const left = Math.min(
      Math.max(VIEWPORT_MARGIN, rect.left),
      Math.max(VIEWPORT_MARGIN, vw - rect.width - VIEWPORT_MARGIN),
    );
    const top = flip ? rect.top - MENU_GAP - maxHeight : rect.bottom + MENU_GAP;
    setPos((prev) =>
      prev &&
      prev.top === top &&
      prev.left === left &&
      prev.width === rect.width &&
      prev.maxHeight === maxHeight
        ? prev
        : { top, left, width: rect.width, maxHeight },
    );
  }, []);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, []);

  const openMenu = useCallback(() => {
    if (disabled) return;
    updatePosition();
    setOpen(true);
  }, [disabled, updatePosition]);

  const select = useCallback(
    (v: string) => {
      onChange(v);
      closeMenu();
    },
    [onChange, closeMenu],
  );

  // Keep the panel glued to the trigger. A rAF loop is used instead of
  // scroll/resize listeners because the trigger can also move when the page
  // reflows or the scroll offset is clamped by a document height change —
  // neither of which reliably emits a scroll event, which would leave the
  // panel drifting away from its trigger.
  useEffect(() => {
    if (!open) return;
    let frame = 0;
    const sync = () => {
      updatePosition();
      frame = window.requestAnimationFrame(sync);
    };
    frame = window.requestAnimationFrame(sync);
    return () => window.cancelAnimationFrame(frame);
  }, [open, updatePosition]);

  // Close on an outside mousedown. The panel lives in a portal, so it must be
  // treated as "inside" explicitly — otherwise the mousedown would close the
  // menu before the option's click handler ever runs.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      closeMenu();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open, closeMenu]);

  // Escape closes the menu and returns focus to the trigger.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      closeMenu();
      btnRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closeMenu]);

  // Focus the filter input without scrolling the page (the panel is fixed).
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus({ preventScroll: true });
  }, [open]);

  // A parent that hides this control (e.g. the Advanced Filters drawer
  // collapsing) must not leave an orphaned panel floating over the page.
  useEffect(() => {
    if (disabled && open) closeMenu();
  }, [disabled, open, closeMenu]);

  const display = value || (emptyLabel ?? placeholder ?? '');

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        onClick={() => (open ? closeMenu() : openMenu())}
        className={`bg-surface-lowest border border-border-subtle rounded px-3 py-1.5 text-body-sm text-left focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer w-full flex items-center justify-between gap-2 disabled:cursor-not-allowed disabled:opacity-60 ${value ? 'text-on-surface' : 'text-on-surface-variant'}`}
      >
        <span className="truncate">{display}</span>
        <svg className={`w-3 h-3 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && pos && typeof document !== 'undefined' && createPortal(
        <div
          ref={panelRef}
          style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
          className="fixed z-[100] bg-surface border border-border-subtle rounded shadow-lg flex flex-col overflow-hidden"
        >
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={placeholder ?? 'Search...'}
            className="bg-surface-lowest border-b border-border-subtle px-3 py-2 text-body-sm text-on-surface placeholder:text-on-surface-variant/50 outline-none focus:ring-2 focus:ring-primary/20"
          />
          <div id={listboxId} role="listbox" aria-label={placeholder ?? 'Options'} className="overflow-y-auto flex-1">
            {emptyLabel && (
              <button
                type="button"
                role="option"
                aria-selected={!value}
                onClick={() => select('')}
                className={`w-full text-left px-3 py-1.5 text-body-sm hover:bg-primary/10 ${!value ? 'text-primary font-semibold' : 'text-on-surface-variant'}`}
              >
                {emptyLabel}
              </button>
            )}
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-body-sm text-on-surface-variant/60">No matches</div>
            ) : (
              filtered.map((o) => (
                <button
                  key={o}
                  type="button"
                  role="option"
                  aria-selected={value === o}
                  onClick={() => select(o)}
                  className={`w-full text-left px-3 py-1.5 text-body-sm hover:bg-primary/10 ${value === o ? 'text-primary font-semibold' : 'text-on-surface'}`}
                >
                  {o}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SearchableSelect;
